/**
 * The bid workspace's sections (R4 shell): what the bid menu lists, what each
 * `/app/bids/:bidId/<section>` path renders, and where every pre-R4 path now
 * lands. Standalone (no React) so routes, the layout, the breadcrumbs and
 * tests can share it without pulling in page components.
 */

/** A section the bid menu links to. `overview` is the bare bid path. */
export type BidSection =
  | 'overview'
  | 'qualify'
  | 'plan'
  | 'write'
  | 'review'
  | 'submit'
  | 'files'
  | 'team'
  | 'activity'

/** The five work areas, in menu order. */
export type BidWorkArea = 'qualify' | 'plan' | 'write' | 'review' | 'submit'

/** Pre-R4 section paths. Each is a permanent redirect now, never a page. */
export type LegacyBidSection =
  | 'documents'
  | 'analysis'
  | 'strategy'
  | 'chat'
  | 'responses'
  | 'submission-pack'
  | 'go-no-go'

export const BID_WORK_AREAS: BidWorkArea[] = ['qualify', 'plan', 'write', 'review', 'submit']

export const BID_SECTIONS: BidSection[] = [
  'overview',
  ...BID_WORK_AREAS,
  'files',
  'team',
  'activity',
]

export const LEGACY_BID_SECTIONS: LegacyBidSection[] = [
  'documents',
  'analysis',
  'strategy',
  'chat',
  'responses',
  'submission-pack',
  'go-no-go',
]

export const BID_SECTION_LABELS: Record<BidSection, string> = {
  overview: 'Overview',
  qualify: 'Qualify',
  plan: 'Plan',
  write: 'Write',
  review: 'Review',
  submit: 'Submit',
  files: 'Tender files',
  team: 'Team',
  activity: 'Activity',
}

const BID_PATH = /^\/app\/bids\/[^/]+(\/.*)?$/

/** Path to a bid section, optionally opened on a sub-view (`?view=`). */
export function bidSectionPath(bidId: string, section: BidSection, view?: string): string {
  const base = `/app/bids/${bidId}`
  const path = section === 'overview' ? base : `${base}/${section}`
  return view ? `${path}?${SUB_VIEW_PARAM}=${encodeURIComponent(view)}` : path
}

/**
 * Editor routes: everything under `/responses/…` (section, question, document
 * and schedule editors, the document overview pages and the structure
 * proposal). They keep their own headers, so no bid header shows on them, and
 * the bid menu starts collapsed there.
 */
export function isBidEditorPath(pathname: string): boolean {
  return /^\/app\/bids\/[^/]+\/responses\/.+/.test(pathname)
}

/**
 * Which menu item is the current page. Document setup and editor routes
 * both belong to Write.
 */
export function getBidSection(pathname: string): BidSection {
  const match = BID_PATH.exec(pathname)
  const rest = match?.[1] ?? ''
  const segment = rest.split('/')[1] ?? ''
  if (segment === 'responses') {
    return 'write'
  }
  return (BID_SECTIONS as string[]).includes(segment) && segment !== 'overview'
    ? (segment as BidSection)
    : 'overview'
}

// ===================
// Sub-views (`?view=`)
// ===================

export const SUB_VIEW_PARAM = 'view'

export interface SubView<T extends string> {
  value: T
  label: string
}

/** Qualify's views. All but go-no-go are AnalysisSection views. */
export type QualifyView =
  | 'tender_overview'
  | 'compliance_matrix'
  | 'submission_checklist'
  | 'evaluation_criteria'
  | 'key_dates'
  | 'risk_register'
  | 'go-no-go'

export const QUALIFY_VIEWS: SubView<QualifyView>[] = [
  { value: 'tender_overview', label: 'Summary' },
  { value: 'compliance_matrix', label: 'Requirements' },
  { value: 'submission_checklist', label: 'Submission checklist' },
  { value: 'evaluation_criteria', label: 'Scoring' },
  { value: 'key_dates', label: 'Dates' },
  { value: 'risk_register', label: 'Risks' },
  { value: 'go-no-go', label: 'Go/no-go' },
]

export type PlanView = 'brief' | 'win-themes'

export const PLAN_VIEWS: SubView<PlanView>[] = [
  { value: 'brief', label: 'Brief' },
  { value: 'win-themes', label: 'Win themes' },
]

export type SubmitView = 'pack'

export const SUBMIT_VIEWS: SubView<SubmitView>[] = [
  { value: 'pack', label: 'Submission pack' },
]

/** Unknown or missing values land on the first view rather than a blank page. */
export function parseSubView<T extends string>(views: SubView<T>[], value: string | null): T {
  const found = views.find((v) => v.value === value)
  return (found ?? views[0]!).value
}

// ===================
// Legacy redirects
// ===================

export interface RedirectTarget {
  pathname: string
  /** Includes the leading `?`, or is empty. */
  search: string
}

function withSearch(params: URLSearchParams): string {
  const s = params.toString()
  return s ? `?${s}` : ''
}

/** Previous Submit checklist bookmarks now open the canonical Qualify data. */
export function submitViewRedirectTarget(bidId: string, search: string): RedirectTarget | null {
  const params = new URLSearchParams(search)
  const view = params.get(SUB_VIEW_PARAM)
  if (view === 'checklist' || view === 'submission_checklist') params.set(SUB_VIEW_PARAM, 'submission_checklist')
  else if (view !== 'addendum-review') return null
  return { pathname: bidSectionPath(bidId, 'qualify'), search: withSearch(params) }
}

/**
 * Where a pre-R4 bid URL lands. Every query param other than the one being
 * translated survives, so deep links (`&item=`, `&run=`, `&highlight=`,
 * compliance filters) keep working.
 */
export function legacyBidRedirectTarget(
  bidId: string,
  from: LegacyBidSection,
  search: string
): RedirectTarget {
  const params = new URLSearchParams(search)
  const base = `/app/bids/${bidId}`

  switch (from) {
    case 'documents':
      return { pathname: `${base}/files`, search: withSearch(params) }

    case 'analysis':
      return { pathname: `${base}/qualify`, search: withSearch(params) }

    case 'strategy': {
      const tab = params.get('tab')
      params.delete('tab')
      if (tab === 'go-no-go') {
        params.set(SUB_VIEW_PARAM, 'go-no-go')
        return { pathname: `${base}/qualify`, search: withSearch(params) }
      }
      params.set(SUB_VIEW_PARAM, tab === 'bid-brief' ? 'brief' : 'win-themes')
      return { pathname: `${base}/plan`, search: withSearch(params) }
    }

    case 'go-no-go':
      params.set(SUB_VIEW_PARAM, 'go-no-go')
      return { pathname: `${base}/qualify`, search: withSearch(params) }

    case 'responses':
      if (params.get(SUB_VIEW_PARAM) === 'documents') params.delete(SUB_VIEW_PARAM)
      return { pathname: `${base}/write`, search: withSearch(params) }

    case 'submission-pack':
      return submitViewRedirectTarget(bidId, search) ?? { pathname: `${base}/submit`, search: withSearch(params) }

    case 'chat':
      // Tender Chat becomes the bid's docked assistant (T5 reads the param).
      params.set('assistant', 'open')
      return { pathname: base, search: withSearch(params) }
  }
}
